import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import {
  TenantApiError,
  type PaginationMeta,
  type TenantCategory,
} from "@qr-menu/api-tenant";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  RowActions,
  RowConfirm,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@qr-menu/ui";
import { tenantApi } from "../lib/api";

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; items: TenantCategory[]; meta: PaginationMeta };

const NAME_MAX = 100;
const DESCRIPTION_MAX = 500;

export function CategoriesRoute() {
  const [state, setState] = useState<State>({ kind: "loading" });

  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [sortOrder, setSortOrder] = useState("0");
  const [isActive, setIsActive] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // One in-flight mutation at a time: a delete blocks create/update submission
  // and every row action, and a create/update submission blocks deletes.
  const busy = submitting || deletingId !== null;

  // Client-side only: filters the already-loaded page of categories, never a
  // separate request.
  const [searchQuery, setSearchQuery] = useState("");
  const searchTrimmed = searchQuery.trim();
  const hasActiveSearch = searchTrimmed !== "";

  function handleSearchReset() {
    setSearchQuery("");
  }

  const filteredItems = useMemo(() => {
    if (state.kind !== "ready") return [];
    // toLocaleLowerCase("tr") (not toLowerCase()) so "İ"/"I" fold the way a
    // Turkish keyboard expects, with no normalization dependency beyond the
    // engine's built-in Intl support.
    const needle = searchTrimmed.toLocaleLowerCase("tr");
    if (needle === "") return state.items;
    return state.items.filter(
      (item) =>
        item.name.toLocaleLowerCase("tr").includes(needle) ||
        item.description.toLocaleLowerCase("tr").includes(needle),
    );
  }, [state, searchTrimmed]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await tenantApi.categories({
        includeInactive: true,
        limit: 50,
        offset: 0,
      });
      setState({ kind: "ready", items: res.items, meta: res.meta });
    } catch (err) {
      setState({
        kind: "error",
        message: describe(err, "Kategoriler alınamadı."),
      });
    }
  }, []);

  useEffect(() => {
    void loadCategories();
  }, [loadCategories]);

  function resetForm() {
    setName("");
    setDescription("");
    setSortOrder("0");
    setIsActive(true);
  }

  function handleEdit(row: TenantCategory) {
    setEditingId(row.id);
    setName(row.name);
    setDescription(row.description);
    setSortOrder(String(row.sort_order));
    setIsActive(row.is_active);
    setFormError(null);
    setSuccessMessage(null);
  }

  function handleCancel() {
    setEditingId(null);
    resetForm();
    setFormError(null);
    setSuccessMessage(null);
  }

  function handleDeleteRequest(id: number) {
    // Moves the pending selection to this row; no request is sent yet.
    setPendingDeleteId(id);
    setDeleteError(null);
    setSuccessMessage(null);
  }

  function handleDeleteCancel() {
    setPendingDeleteId(null);
    setDeleteError(null);
  }

  async function handleDeleteConfirm(id: number) {
    if (busy) return;

    setDeletingId(id);
    setDeleteError(null);
    setSuccessMessage(null);
    try {
      await tenantApi.deleteCategory(id);
      setPendingDeleteId(null);
      // Refetch rather than removing the row locally.
      await loadCategories();
      setSuccessMessage("Kategori silindi.");
    } catch (err) {
      // A 401 already triggered the client's onUnauthorized. Every other
      // failure keeps the row, keeps the pending selection so the user can
      // retry or back out, and shows the backend's safe public message.
      if (!(err instanceof TenantApiError && err.status === 401)) {
        setDeleteError(describe(err, "Kategori silinemedi."));
      }
    } finally {
      setDeletingId(null);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;

    const trimmedName = name.trim();
    if (trimmedName === "") {
      setFormError("Ad zorunludur.");
      return;
    }
    if (trimmedName.length > NAME_MAX) {
      setFormError(`Ad en fazla ${NAME_MAX} karakter olabilir.`);
      return;
    }
    if (description.length > DESCRIPTION_MAX) {
      setFormError(`Açıklama en fazla ${DESCRIPTION_MAX} karakter olabilir.`);
      return;
    }
    const parsedSort = Number(sortOrder);
    if (!Number.isInteger(parsedSort) || parsedSort < 0) {
      setFormError("Sıra negatif olmayan bir tam sayı olmalıdır.");
      return;
    }

    const input = {
      name: trimmedName,
      description: description.trim(),
      sort_order: parsedSort,
      is_active: isActive,
    };

    setSubmitting(true);
    setFormError(null);
    setSuccessMessage(null);
    try {
      if (editingId === null) {
        await tenantApi.createCategory(input);
        resetForm();
        // Refetch rather than guessing where the backend placed the new row.
        await loadCategories();
        setSuccessMessage("Kategori oluşturuldu.");
      } else {
        await tenantApi.updateCategory(editingId, input);
        setEditingId(null);
        resetForm();
        await loadCategories();
        setSuccessMessage("Kategori güncellendi.");
      }
    } catch (err) {
      // A 401 already triggered the client's onUnauthorized, which clears the
      // session and redirects; no inline message for that case. Every other
      // failure keeps the form values and the current list untouched, and in
      // edit mode keeps edit mode open.
      if (!(err instanceof TenantApiError && err.status === 401)) {
        setFormError(
          describe(
            err,
            editingId === null
              ? "Kategori oluşturulamadı."
              : "Kategori güncellenemedi.",
          ),
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  const isEditing = editingId !== null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Kategoriler</CardTitle>
        <CardDescription>Aktif işletmenin menü kategorileri.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="create-form" onSubmit={handleSubmit}>
          <p className="form-heading">
            {isEditing
              ? `Kategori düzenleniyor (#${editingId})`
              : "Yeni kategori"}
          </p>
          <div className="create-form-grid">
            <Field label="Ad" htmlFor="category-name">
              <input
                id="category-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={NAME_MAX}
                placeholder="Başlangıçlar"
              />
            </Field>
            <Field label="Açıklama" htmlFor="category-description">
              <input
                id="category-description"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={DESCRIPTION_MAX}
                placeholder="Opsiyonel"
              />
            </Field>
            <Field label="Sıra" htmlFor="category-sort-order">
              <input
                id="category-sort-order"
                type="number"
                min={0}
                step={1}
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
            </Field>
            <Field label="Aktif" htmlFor="category-active" variant="checkbox">
              <input
                id="category-active"
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
            </Field>
            <div className="filter-actions">
              <button type="submit" className="primary" disabled={busy}>
                {submitting
                  ? "Kaydediliyor…"
                  : isEditing
                    ? "Kategoriyi güncelle"
                    : "Yeni kategori"}
              </button>
              {isEditing && (
                <button type="button" onClick={handleCancel} disabled={busy}>
                  İptal
                </button>
              )}
            </div>
          </div>
          {formError && <div className="state error">{formError}</div>}
          {successMessage && <div className="state ok">{successMessage}</div>}
        </form>

        <div className="create-form">
          <p className="form-heading">Kategorilerde ara</p>
          <div className="create-form-grid">
            <Field label="Arama" htmlFor="category-search">
              <input
                id="category-search"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Kategori adı veya açıklama ara"
              />
            </Field>
            <div className="filter-actions">
              <button
                type="button"
                onClick={handleSearchReset}
                disabled={!hasActiveSearch}
              >
                Aramayı temizle
              </button>
            </div>
          </div>
        </div>

        {state.kind === "loading" && <div className="state">Yükleniyor…</div>}

        {state.kind === "error" && (
          <div className="state error">Yüklenemedi. {state.message}</div>
        )}

        {state.kind === "ready" && (
          <CategoriesBody
            items={filteredItems}
            totalCount={state.meta.total}
            hasActiveSearch={hasActiveSearch}
            editingId={editingId}
            actionsDisabled={busy}
            pendingDeleteId={pendingDeleteId}
            deletingId={deletingId}
            deleteError={deleteError}
            onEdit={handleEdit}
            onDeleteRequest={handleDeleteRequest}
            onDeleteConfirm={handleDeleteConfirm}
            onDeleteCancel={handleDeleteCancel}
          />
        )}
      </CardContent>
    </Card>
  );
}

interface CategoriesBodyProps {
  items: TenantCategory[];
  totalCount: number;
  hasActiveSearch: boolean;
  editingId: number | null;
  actionsDisabled: boolean;
  pendingDeleteId: number | null;
  deletingId: number | null;
  deleteError: string | null;
  onEdit: (row: TenantCategory) => void;
  onDeleteRequest: (id: number) => void;
  onDeleteConfirm: (id: number) => void;
  onDeleteCancel: () => void;
}

function CategoriesBody({
  items,
  totalCount,
  hasActiveSearch,
  editingId,
  actionsDisabled,
  pendingDeleteId,
  deletingId,
  deleteError,
  onEdit,
  onDeleteRequest,
  onDeleteConfirm,
  onDeleteCancel,
}: CategoriesBodyProps) {
  if (items.length === 0) {
    return (
      <div className="state">
        {hasActiveSearch
          ? "Aramayla eşleşen kategori yok."
          : "Henüz kategori bulunmuyor."}
      </div>
    );
  }

  return (
    <>
      {deleteError && <div className="state error">{deleteError}</div>}
      <Table>
        <TableHeader>
          <TableRow>
            <TableCell as="th">Ad</TableCell>
            <TableCell as="th">Açıklama</TableCell>
            <TableCell as="th" style={{ width: 80 }}>
              Sıra
            </TableCell>
            <TableCell as="th" style={{ width: 100 }}>
              Durum
            </TableCell>
            <TableCell as="th" style={{ width: 200 }}>
              İşlem
            </TableCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((row) => (
            <TableRow key={row.id}>
              <TableCell className="wrap-cell">{row.name}</TableCell>
              <TableCell className="wrap-cell">
                {row.description.trim() === "" ? (
                  <span className="hint">—</span>
                ) : (
                  row.description
                )}
              </TableCell>
              <TableCell>{row.sort_order}</TableCell>
              <TableCell>
                <Badge variant={row.is_active ? "success" : "danger"}>
                  {row.is_active ? "Aktif" : "Pasif"}
                </Badge>
              </TableCell>
              <RowActions as="td">
                {pendingDeleteId === row.id ? (
                  <RowConfirm>
                    <span className="hint">Silinsin mi?</span>
                    <Button
                      type="button"
                      variant="danger"
                      onClick={() => onDeleteConfirm(row.id)}
                      disabled={actionsDisabled}
                    >
                      {deletingId === row.id ? "Siliniyor…" : "Evet, sil"}
                    </Button>
                    <Button
                      type="button"
                      onClick={onDeleteCancel}
                      disabled={actionsDisabled}
                    >
                      Vazgeç
                    </Button>
                  </RowConfirm>
                ) : (
                  <RowConfirm>
                    <Button
                      type="button"
                      onClick={() => onEdit(row)}
                      disabled={actionsDisabled}
                    >
                      {editingId === row.id ? "Düzenleniyor" : "Düzenle"}
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      onClick={() => onDeleteRequest(row.id)}
                      disabled={actionsDisabled || editingId === row.id}
                    >
                      Sil
                    </Button>
                  </RowConfirm>
                )}
              </RowActions>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="hint" style={{ marginTop: 12, marginBottom: 0 }}>
        {hasActiveSearch
          ? `Gösterilen: ${items.length} / Toplam: ${totalCount}`
          : `Toplam: ${totalCount}`}
      </p>
    </>
  );
}

function describe(err: unknown, fallback: string): string {
  // TenantApiError.message carries the backend's curated public error_message,
  // never a raw cause or response body.
  if (err instanceof TenantApiError) {
    return err.message;
  }
  return fallback;
}
